import qs from "query-string";
import { PageNameEnum, TrendChartBinSizesEnum } from "../../types/enums";
import { useMount } from "../../constants/constants";
import {
  updateTrendChartBinSize,
  goToDay,
} from "../../providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import {
  useTrendChartBinSize,
  useFocusRange,
  useFormatZoned,
} from "utils/customHooks/genericHooks";
import useQueryString from "utils/customHooks/useQueryString";
import { useNavigate } from "react-router";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { filterObjectByRemovingKeys } from "utils/arrayUtils";
import { getToday } from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { useTransition } from "react";
import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export const getQuery = location => qs.parse(location.search);

export const getView = location => {
  const query = getQuery(location);
  // Query result could, from a type perspective, return a list rather
  // than just a string, so need to handle that
  let view = "tiles";
  if (query.view) {
    view = typeof query.view === "object" ? String(query.view[0]) : query.view;
  }
  return view;
};

export const useResetTrendChartBinSize = ({
  newTrendChartBinSize,
}: {
  newTrendChartBinSize: TrendChartBinSizesEnum;
}) => {
  const dispatch = useDispatch();
  const { trendChartBinSize } = useTrendChartBinSize();

  useMount(() => {
    if (trendChartBinSize !== newTrendChartBinSize) {
      dispatch(updateTrendChartBinSize(newTrendChartBinSize));
    }
  });
};

export const useToggleIsCustomRangeSelector = () => {
  const parsedQuery = useQueryString();
  const focusRange = useFocusRange();
  const navigate = useNavigate();
  const { dayStartMsOffset } = useSettings();
  const dispatch = useDispatch();
  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const [, startTransition] = useTransition();

  return () => {
    // if switching back to day/shift mode
    const isCustomRangeSelector = parsedQuery.ge && parsedQuery.lt;
    if (!focusRange) {
      return;
    } else if (isCustomRangeSelector) {
      const stringifiedQueryWithoutRange = qs.stringify(
        filterObjectByRemovingKeys(parsedQuery, ["ge", "lt"]) as any
      );
      navigate({ search: `${stringifiedQueryWithoutRange}` });
      // go back to the current shift
      startTransition(() => {
        dispatch(
          goToDay({
            newSelectedDay: getToday(
              dayStartMsOffset,
              timeZoneMsOffset,
              formatZoned
            ),
            dayStartMsOffset,
            timeZoneMsOffset,
            shouldGoToFirstShiftInDay: false,
            shouldGoToCurrentShift: true,
            formatZoned,
          })
        );
      });
    } else {
      // add ge, lt to query when switching to custom range mode
      const stringifiedQueryWithRange = qs.stringify({
        ...parsedQuery,
        ge: focusRange.ge,
        lt: focusRange.lt,
      });
      navigate({ search: `${stringifiedQueryWithRange}` });
    }
  };
};

export const TREND_CHART_CARD_ID = "Group_Trend";
const isTrendChartExpandedLS = window.localStorage.getItem(
  `collapsibles:${PageNameEnum.timeline}:${TREND_CHART_CARD_ID}`
);
const initialIsTrendChartExpanded =
  !isTrendChartExpandedLS || isTrendChartExpandedLS === "true";
const isTrendChartExpandedAtom = atom(initialIsTrendChartExpanded);
export function useIsTrendChartExpandedTimelinePage() {
  return useAtom(isTrendChartExpandedAtom);
}

export const TREND_CHART_CARD_ID_HUBLET = "Card_TodaysProduction";
const isTrendChartExpandedLSHublet = window.localStorage.getItem(
  `collapsibles:${PageNameEnum.hublet}:${TREND_CHART_CARD_ID_HUBLET}`
);
const initialIsTrendChartExpandedHublet =
  !isTrendChartExpandedLSHublet || isTrendChartExpandedLSHublet === "true";
const isTrendChartExpandedAtomHublet = atom(initialIsTrendChartExpandedHublet);
export function useIsTrendChartExpandedHubletPage() {
  return useAtom(isTrendChartExpandedAtomHublet);
}

export const PNL_CHART_CARD_ID_HUBLET_PAGE = "Card_PnlChart_TimelinePage";
const isPnlChartExpandedLSHublet = window.localStorage.getItem(
  `collapsibles:${PageNameEnum.hublet}:${PNL_CHART_CARD_ID_HUBLET_PAGE}`
);
const initialIsPnlChartExpandedHublet =
  !isPnlChartExpandedLSHublet || isPnlChartExpandedLSHublet === "true";
const isPnlChartExpandedAtomHublet = atom(initialIsPnlChartExpandedHublet);
export function useIsPnlChartExpandedHubletPage() {
  return useAtom(isPnlChartExpandedAtomHublet);
}

const isTrendChartExpandedTrendsPageLS = window.localStorage.getItem(
  `collapsibles:trendsPage:trendChart`
);
const isTrendChartExpandedTrendsPage =
  !isTrendChartExpandedTrendsPageLS ||
  isTrendChartExpandedTrendsPageLS === "true";
const isTrendChartExpandedTrendsPageAtom = atomWithStorage(
  "collapsibles:trendsPage:trendChart",
  isTrendChartExpandedTrendsPage
);
export function useIsTrendChartExpandedTrendsPage() {
  return useAtom(isTrendChartExpandedTrendsPageAtom);
}
